import { useCallback, useEffect, useRef, useState } from 'react';
import * as Tone from 'tone';
import { createMonitorEngine } from '../../utils/pitchMonitorEngine';
import { openMicCapture, describeMicError } from './micCapture';

/**
 * The pitch monitor's mic, engine (see pitchMonitorEngine) and state: the
 * readings land in `bufferRef` (the monitor draws from it every animation
 * frame), judged held notes in `notes`, the latest reading in `reading`,
 * and `status` says which mic is in use, whether audio is running and
 * whether sound is arriving, so a silent monitor explains itself.
 *
 * The monitor captures on a plain AudioContext of its own (as the scratch
 * recorder does), created and resumed by primeMonitorAudio() from the click
 * that opens it: some browsers only let audio start inside a click.
 */

const KEPT_NOTES = 8;
const READOUT_MS = 100;
// No sound for this long while the mic is open means something is wrong
const SILENT_MS = 1500;
const TARGET_TONE_SECONDS = 1.5;

let monitorContext = null;

/** Create (or wake) the monitor's audio context; call it from the click that opens the monitor. */
export function primeMonitorAudio() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!monitorContext || monitorContext.state === 'closed') monitorContext = new Ctx();
  if (monitorContext.state === 'suspended') monitorContext.resume().catch(() => {});
  return monitorContext;
}

export function usePitchMonitor({ open }) {
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const [reading, setReading] = useState(null);
  const [notes, setNotes] = useState([]);
  const [target, setTargetState] = useState(null); // MIDI number, or null = nearest note
  const [status, setStatus] = useState(null); // { label, sampleRate, processing, running, arriving }

  const targetRef = useRef(target);
  const bufferRef = useRef([]);
  const notesRef = useRef([]);
  const lastReadoutRef = useRef(0);
  const toneRef = useRef(null);

  const setTarget = useCallback((midi) => {
    targetRef.current = midi;
    setTargetState(midi);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    let closed = false;
    let mic = null;
    let engine = null;
    let lastChunkAt = 0;
    let openedAt = 0;
    bufferRef.current = [];
    notesRef.current = [];
    setNotes([]);
    setReading(null);
    setError(null);
    setStatus(null);
    const context = primeMonitorAudio();

    const onNote = (note) => {
      notesRef.current = [...notesRef.current, note].slice(-KEPT_NOTES);
      setNotes(notesRef.current);
    };

    const onChunk = ({ samples, frame }) => {
      if (!engine) return;
      lastChunkAt = performance.now();
      engine.push(samples, frame);
      const buffer = engine.buffer;
      if (!buffer.length || lastChunkAt - lastReadoutRef.current < READOUT_MS) return;
      lastReadoutRef.current = lastChunkAt;
      const latest = buffer.slice(-5);
      const last = latest[latest.length - 1];
      setReading({
        sung: last.sung,
        midi: last.midi,
        hz: last.f0,
        clarity: last.clarity,
        level: 20 * Math.log10(Math.max(...latest.map(b => b.rms)) + 1e-9)
      });
    };

    // Keep the status line honest: is audio running, is sound arriving?
    const watch = setInterval(() => {
      if (!mic) return;
      setStatus({
        label: mic.label,
        sampleRate: mic.sampleRate,
        processing: mic.processing,
        running: !context || context.state === 'running',
        arriving: performance.now() - (lastChunkAt || openedAt) < SILENT_MS
      });
    }, 500);

    (async () => {
      try {
        if (!context) throw new Error('This browser has no Web Audio support');
        mic = await openMicCapture(onChunk, { context });
        if (closed) {
          mic.close();
          return;
        }
        engine = createMonitorEngine({ sampleRate: mic.sampleRate, getTarget: () => targetRef.current, onNote });
        bufferRef.current = engine.buffer;
        openedAt = performance.now();
        mic.start();
        setReady(true);
      } catch (e) {
        if (!closed) setError(`Could not open the microphone (${describeMicError(e)})`);
      }
    })();

    return () => {
      closed = true;
      clearInterval(watch);
      setReady(false);
      if (mic) mic.close();
      toneRef.current?.triggerRelease();
    };
  }, [open]);

  // Dispose of the reference tone with the monitor
  useEffect(() => () => {
    if (toneRef.current) {
      toneRef.current.dispose();
      toneRef.current = null;
    }
  }, []);

  // From a click: wake the monitor's audio if the browser held it back
  const resumeAudio = useCallback(() => {
    primeMonitorAudio();
  }, []);

  const playTarget = useCallback(async () => {
    if (targetRef.current === null) return;
    await Tone.start();
    if (!toneRef.current) {
      toneRef.current = new Tone.Synth({
        oscillator: { type: 'triangle' },
        envelope: { attack: 0.02, decay: 0.1, sustain: 0.8, release: 0.3 },
        volume: -10
      }).toDestination();
    }
    toneRef.current.triggerAttackRelease(440 * 2 ** ((targetRef.current - 69) / 12), TARGET_TONE_SECONDS);
  }, []);

  const clearNotes = useCallback(() => {
    notesRef.current = [];
    setNotes([]);
  }, []);

  return { ready, error, status, reading, notes, notesRef, bufferRef, target, setTarget, playTarget, clearNotes, resumeAudio };
}
